import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    getJobOffers,
    getJobOffer,
    getCompanyEstablishmentsWithTeams,
    getCandidates,
    createCandidate,
    createJobOffer,
} from "../../../_api/Talent";
import { addTeam } from "../../../_api/Team";
import { getUsers } from "../../../_api/Admin";
import { Button } from "../../../_helpers/components/Button";
import { SideBar } from "../../../_helpers/components/SideBar";

const STATUS_LABELS = {
    NEW: "Nouveau",
    SCREENING: "Présélection",
    INTERVIEW: "Entretien",
    OFFER: "Offre",
    HIRED: "Embauché",
    REJECTED: "Rejeté",
    WAITING: "En attente",
    ACCEPTED: "Accepté",
};
const STATUS_COLORS = {
    NEW: "bg-slate-100 text-slate-800",
    SCREENING: "bg-blue-100 text-blue-800",
    INTERVIEW: "bg-amber-100 text-amber-800",
    OFFER: "bg-emerald-100 text-emerald-800",
    HIRED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
    WAITING: "bg-gray-100 text-gray-800",
    ACCEPTED: "bg-teal-100 text-teal-800",
};
const INTERVIEW_TYPE_LABELS = { ANNUEL: "Annuel", PROFESSIONNEL: "Professionnel", EVAL_360: "Éval 360", RECRUTEMENT: "Recrutement" };

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");

const CandidateForm = ({ closeModal, onSuccess, offers, establishments, singleEstablishment }) => {
    const soleEst = singleEstablishment && establishments?.length === 1 ? establishments[0] : null;
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", job_offer_id: "", establishment_id: soleEst?.id ?? "" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedOffer = offers.find((o) => o.id === Number(form.job_offer_id));
    const establishmentId = form.establishment_id || selectedOffer?.establishment_id || soleEst?.id;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) return;
        setIsSubmitting(true);
        createCandidate({
            ...form,
            job_offer_id: form.job_offer_id || undefined,
            establishment_id: establishmentId || undefined,
        })
            .then(() => {
                toast.success("Candidat créé.");
                setForm({ first_name: "", last_name: "", email: "", phone: "", job_offer_id: "", establishment_id: "" });
                onSuccess?.();
                closeModal?.();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input placeholder="Nom" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                <select value={form.job_offer_id} onChange={(e) => setForm((f) => ({ ...f, job_offer_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="">— Choisir un poste —</option>
                    {offers.map((o) => (
                        <option key={o.id} value={o.id}>{o.title}</option>
                    ))}
                </select>
            </div>
            {!singleEstablishment && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Établissement</label>
                    <select value={establishmentId} onChange={(e) => setForm((f) => ({ ...f, establishment_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                        <option value="">— Choisir —</option>
                        {(establishments || []).map((est) => (
                            <option key={est.id} value={est.id}>{est.name}</option>
                        ))}
                    </select>
                </div>
            )}
            <div className="flex gap-2 pt-2">
                <Button buttonType="submit" name="Créer" loading={isSubmitting} />
                <Button type="empty" name="Annuler" action={() => closeModal?.()} />
            </div>
        </form>
    );
};

const OfferForm = ({ closeModal, onSuccess, establishments, users, onEstablishmentsRefresh, singleEstablishment }) => {
    const soleEst = singleEstablishment && establishments?.length === 1 ? establishments[0] : null;
    const [form, setForm] = useState({
        title: "", description: "", establishment_id: soleEst?.id ?? "", category_id: "", team_id: "", manager_id: "", status: "DRAFT",
    });
    const [newTeamName, setNewTeamName] = useState("");
    const [addingTeam, setAddingTeam] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedEst = soleEst || establishments?.find((e) => e.id === Number(form.establishment_id));
    const teams = selectedEst?.teams || [];

    const handleAddTeam = (e) => {
        e.preventDefault();
        const estId = selectedEst?.id ?? form.establishment_id;
        if (!estId || !newTeamName.trim()) return;
        setAddingTeam(true);
        addTeam(Number(estId), { name: newTeamName.trim(), remarks: "" })
            .then(() => {
                toast.success("Équipe ajoutée.");
                setNewTeamName("");
                onEstablishmentsRefresh?.();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setAddingTeam(false));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.title.trim()) return;
        setIsSubmitting(true);
        createJobOffer({
            ...form,
            establishment_id: form.establishment_id || selectedEst?.id || null,
            team_id: form.team_id || null,
            manager_id: form.manager_id || null,
        })
            .then(() => {
                toast.success("Poste créé.");
                onSuccess?.();
                closeModal?.();
            })
            .catch((err) => toast.error(err?.response?.data?.message || "Erreur"))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intitulé du poste</label>
                <input placeholder="Ex: Développeur full-stack" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" required />
            </div>
            {!singleEstablishment && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Établissement</label>
                    <select value={form.establishment_id} onChange={(e) => setForm((f) => ({ ...f, establishment_id: e.target.value, team_id: "" }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                        <option value="">— Choisir —</option>
                        {(establishments || []).map((est) => (
                            <option key={est.id} value={est.id}>{est.name}</option>
                        ))}
                    </select>
                </div>
            )}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Équipe / Service</label>
                <select value={form.team_id} onChange={(e) => setForm((f) => ({ ...f, team_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" disabled={!selectedEst}>
                    <option value="">— Aucune —</option>
                    {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
                {selectedEst && (
                    <div className="mt-2 flex gap-2">
                        <input
                            placeholder="Nouvelle équipe..."
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                        />
                        <button type="button" onClick={handleAddTeam} disabled={addingTeam || !newTeamName.trim()} className="px-3 py-1.5 text-sm border border-app-primary text-app-primary rounded-lg hover:bg-app-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
                            {addingTeam ? "..." : "Ajouter"}
                        </button>
                    </div>
                )}
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manager demandeur</label>
                <select value={form.manager_id} onChange={(e) => setForm((f) => ({ ...f, manager_id: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary">
                    <option value="">— Aucun —</option>
                    {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-app-primary focus:border-app-primary" rows={3} />
            </div>
            <div className="flex gap-2 pt-2">
                <Button buttonType="submit" name="Créer" loading={isSubmitting} />
                <Button type="empty" name="Annuler" action={() => closeModal?.()} />
            </div>
        </form>
    );
};

export const Recrutement = () => {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [establishments, setEstablishments] = useState([]);
    const [users, setUsers] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [expandedOffer, setExpandedOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openSidebar, setOpenSidebar] = useState(false);
    const [sidebarMode, setSidebarMode] = useState("candidate");

    const fetch = () => {
        setLoading(true);
        Promise.all([
            getJobOffers(),
            getCompanyEstablishmentsWithTeams().catch(() => ({ data: [] })),
            getUsers().catch(() => ({ data: [] })),
        ])
            .then(([oRes, eRes, uRes]) => {
                setOffers(oRes.data || []);
                setEstablishments(eRes.data || []);
                setUsers(uRes.data || []);
            })
            .catch((err) => setError(err?.response?.data?.message || "Erreur"))
            .finally(() => setLoading(false));
    };

    useEffect(() => fetch(), []);

    const fetchOfferDetail = (id) => {
        getJobOffer(id)
            .then((res) => setExpandedOffer(res.data))
            .catch(() => toast.error("Erreur chargement détails"));
    };

    const toggleExpand = (id) => {
        if (expandedId === id) {
            setExpandedId(null);
            setExpandedOffer(null);
        } else {
            setExpandedId(id);
            fetchOfferDetail(id);
        }
    };

    const openNewCandidate = () => {
        setSidebarMode("candidate");
        setOpenSidebar(true);
    };

    const openNewOffer = () => {
        setSidebarMode("offer");
        setOpenSidebar(true);
    };

    const activeUsers = users.filter((u) => u.activated !== false);

    if (loading) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center py-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-app-primary mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-full bg-gray-50 flex items-center justify-center">
                <div className="text-center text-red-600">{error}</div>
            </div>
        );
    }

    return (
        <div className="min-h-full bg-gray-50">
            <div className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Recrutement</h1>
                            <p className="mt-2 text-sm text-gray-600">Postes à pourvoir, candidats et entretiens</p>
                        </div>
                        <div className="flex gap-2">
                            <Button action={openNewOffer} name="Ajouter un poste" />
                            <Button type="empty" action={openNewCandidate} name="Nouveau candidat" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Postes à pourvoir ({offers.length})</h2>
                </div>
                <div className="space-y-4">
                    {offers.length > 0 ? (
                        offers.map((offer) => (
                            <div key={offer.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div
                                    className="p-6 cursor-pointer hover:bg-gray-50/50 transition-colors"
                                    onClick={() => toggleExpand(offer.id)}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h2 className="text-xl font-bold text-gray-900">{offer.title}</h2>
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                                {offer.establishment && establishments?.length > 1 && <span>Établissement : <strong>{offer.establishment.name}</strong></span>}
                                                {offer.team && <span>Équipe : <strong>{offer.team.name}</strong></span>}
                                                {offer.manager && <span>Manager : <strong>{offer.manager.first_name} {offer.manager.last_name}</strong></span>}
                                                {offer.category && <span>Catégorie : {offer.category.name}</span>}
                                            </div>
                                            <p className="mt-1 text-sm text-gray-500">{offer.candidates_count ?? 0} candidat{(offer.candidates_count ?? 0) > 1 ? "s" : ""}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedId === offer.id ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {expandedId === offer.id && expandedOffer?.id === offer.id && (
                                    <div className="border-t border-gray-200 bg-gray-50/50 p-6">
                                        {expandedOffer.description && (
                                            <div className="mb-6">
                                                <h3 className="text-sm font-semibold text-gray-700 mb-2">Description</h3>
                                                <p className="text-sm text-gray-600 whitespace-pre-wrap">{expandedOffer.description}</p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Candidats</h3>
                                                {expandedOffer.candidates?.length > 0 ? (
                                                    <ul className="space-y-2">
                                                        {expandedOffer.candidates.map((c) => (
                                                            <li key={c.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100">
                                                                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(`/talents/recrutement/${c.id}`)}>
                                                                    <span className="font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                                                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[c.status] || "bg-gray-100"}`}>{STATUS_LABELS[c.status]}</span>
                                                                </div>
                                                                <button onClick={() => navigate(`/talents/recrutement/${c.id}`)} className="text-xs text-app-primary hover:text-app-secondary">Voir →</button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-sm text-gray-500">Aucun candidat pour ce poste.</p>
                                                )}
                                            </div>

                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Entretiens</h3>
                                                {expandedOffer.candidates && (() => {
                                                    const interviews = (expandedOffer.candidates || []).flatMap((c) => (c.interviews || []).map((i) => ({ ...i, candidate: c })));
                                                    return interviews.length > 0 ? (
                                                        <ul className="space-y-2">
                                                            {interviews.map((i) => (
                                                                <li
                                                                    key={i.id}
                                                                    onClick={() => navigate(`/talents/entretiens/${i.id}`)}
                                                                    className="py-2 px-3 bg-white rounded-lg border border-gray-100 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex justify-between items-center"
                                                                >
                                                                    <span><span className="font-medium">{i.candidate?.first_name} {i.candidate?.last_name}</span>
                                                                    <span className="text-gray-500"> · {INTERVIEW_TYPE_LABELS[i.type] || i.type} · {formatDate(i.date)}</span>
                                                                    {i.evaluator && <span className="text-gray-500"> · {i.evaluator.first_name} {i.evaluator.last_name}</span>}</span>
                                                                    <span className="text-xs text-app-primary shrink-0">Voir →</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-sm text-gray-500">Aucun entretien pour ce poste.</p>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <h3 className="mt-4 text-sm font-medium text-gray-900">Aucun poste à pourvoir</h3>
                            <p className="mt-2 text-sm text-gray-500">Cliquez sur &laquo; Ajouter un poste &raquo; en haut pour commencer.</p>
                        </div>
                    )}
                </div>
            </div>

            <SideBar
                title={sidebarMode === "offer" ? "Nouveau poste" : "Nouveau candidat"}
                content={
                    sidebarMode === "offer" ? (
                        <OfferForm closeModal={() => setOpenSidebar(false)} onSuccess={() => { fetch(); setOpenSidebar(false); }} establishments={establishments} users={activeUsers} onEstablishmentsRefresh={fetch} singleEstablishment={establishments?.length === 1} />
                    ) : (
                        <CandidateForm closeModal={() => setOpenSidebar(false)} onSuccess={() => { fetch(); if (expandedId) fetchOfferDetail(expandedId); }} offers={offers} establishments={establishments} singleEstablishment={establishments?.length === 1} />
                    )
                }
                isOpen={openSidebar}
                onClose={() => setOpenSidebar(false)}
            />
        </div>
    );
};
